/* global debounce */
/**
 * Press testimonials card carousel.
 * Native scroll-snap track with prev/next arrows, generated pagination dots (one per scroll
 * position) and optional auto-rotation that pauses on hover, focus and user interaction.
 */
if (!customElements.get('testimonial-cards')) {
  class TestimonialCards extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.press-testimonials__track');
      this.dotsContainer = this.querySelector('.press-testimonials__dots');
      this.dotTemplate = this.querySelector('.press-testimonials__dot-template');
      this.prevBtn = this.querySelector('.press-testimonials__arrow--prev');
      this.nextBtn = this.querySelector('.press-testimonials__arrow--next');
      this.slides = Array.from(this.track.children);
      if (this.slides.length < 2 || !this.dotsContainer) return;

      this.rtl = document.dir === 'rtl';
      this.autoplay = this.dataset.autoplay === 'true'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = Number(this.dataset.speed || 5) * 1000;
      this.currentPage = 0;

      this.scrollHandler = debounce(this.handleScroll.bind(this), 50);
      this.resizeHandler = this.build.bind(this);
      this.dotClickHandler = this.handleDotClick.bind(this);
      this.arrowClickHandler = this.handleArrowClick.bind(this);
      this.pauseHandler = () => this.stopAutoplay();
      this.resumeHandler = () => this.startAutoplay();

      this.track.addEventListener('scroll', this.scrollHandler, { passive: true });
      this.dotsContainer.addEventListener('click', this.dotClickHandler);
      this.prevBtn?.addEventListener('click', this.arrowClickHandler);
      this.nextBtn?.addEventListener('click', this.arrowClickHandler);
      window.addEventListener('on:debounced-resize', this.resizeHandler);

      if (this.autoplay) {
        this.addEventListener('mouseenter', this.pauseHandler);
        this.addEventListener('mouseleave', this.resumeHandler);
        this.addEventListener('focusin', this.pauseHandler);
        this.addEventListener('focusout', this.resumeHandler);
        this.track.addEventListener('pointerdown', this.pauseHandler);
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.blockSelectHandler = this.handleBlockSelect.bind(this);
        document.addEventListener('shopify:block:select', this.blockSelectHandler);
      }

      this.build();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
      if (this.blockSelectHandler) {
        document.removeEventListener('shopify:block:select', this.blockSelectHandler);
      }
    }

    /**
     * Calculates the number of scroll positions and (re)renders the dots.
     */
    build() {
      const span = this.getSlideSpan();
      const perPage = Math.max(1, Math.floor((this.track.clientWidth - this.getInlinePadding() + this.getGap()) / span));
      this.totalPages = Math.max(1, this.slides.length - perPage + 1);

      this.dotsContainer.innerHTML = '';
      this.dotsContainer.hidden = this.totalPages < 2;
      [this.prevBtn, this.nextBtn].forEach((btn) => {
        if (btn) btn.hidden = this.totalPages < 2;
      });

      for (let i = 0; i < this.totalPages; i += 1) {
        const dot = this.dotTemplate.content.firstElementChild.cloneNode(true);
        dot.dataset.index = i;
        dot.querySelector('.js-index').textContent = i + 1;
        dot.querySelector('.js-total').textContent = this.totalPages;
        this.dotsContainer.appendChild(dot);
      }

      this.currentPage = Math.min(this.currentPage, this.totalPages - 1);
      this.updateDots();
      this.startAutoplay();
    }

    getGap() {
      return parseFloat(getComputedStyle(this.track).columnGap) || 0;
    }

    getInlinePadding() {
      const style = getComputedStyle(this.track);
      return parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    }

    getSlideSpan() {
      return this.slides[0].getBoundingClientRect().width + this.getGap();
    }

    handleScroll() {
      const page = Math.round(Math.abs(this.track.scrollLeft) / this.getSlideSpan());
      this.currentPage = Math.min(page, this.totalPages - 1);
      this.updateDots();
    }

    handleDotClick(evt) {
      const dot = evt.target.closest('.press-testimonials__dot');
      if (!dot) return;
      this.stopAutoplay();
      this.goTo(Number(dot.dataset.index));
    }

    handleArrowClick(evt) {
      const step = evt.currentTarget.name === 'next' ? 1 : -1;
      this.stopAutoplay();
      this.goTo(Math.max(0, Math.min(this.currentPage + step, this.totalPages - 1)));
    }

    handleBlockSelect(evt) {
      const slide = this.slides.find((el) => el.contains(evt.target));
      if (!slide) return;
      this.stopAutoplay();
      this.goTo(Math.min(this.slides.indexOf(slide), this.totalPages - 1));
    }

    /**
     * Scrolls the track to the given page.
     * @param {number} index - Page index.
     */
    goTo(index) {
      this.currentPage = index;
      const left = index * this.getSlideSpan() * (this.rtl ? -1 : 1);
      this.track.scrollTo({ left, behavior: 'smooth' });
      this.updateDots();
    }

    updateDots() {
      this.dotsContainer.querySelectorAll('.press-testimonials__dot').forEach((dot, i) => {
        dot.setAttribute('aria-current', i === this.currentPage ? 'true' : 'false');
      });

      if (this.prevBtn) this.prevBtn.disabled = this.currentPage === 0;
      if (this.nextBtn) this.nextBtn.disabled = this.currentPage >= this.totalPages - 1;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.totalPages < 2) return;
      this.timer = setInterval(() => {
        this.goTo((this.currentPage + 1) % this.totalPages);
      }, this.speed);
    }

    stopAutoplay() {
      clearInterval(this.timer);
    }
  }

  customElements.define('testimonial-cards', TestimonialCards);
}
